// Offline-First Profile Service
// Primary storage: IndexedDB, Sync target: Supabase
// All profile operations go through IndexedDB first

import { offlineFirstDataManager } from './offlineFirstDataManager';
import { User } from '@supabase/supabase-js';

export interface ProfileData {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  _offline_modified?: boolean;
  _sync_pending?: boolean;
}

export interface ProfileResult {
  success: boolean;
  profile?: ProfileData;
  error?: string;
  fromCache: boolean;
  needsSync?: boolean;
}

export interface ProfileUpdateResult {
  success: boolean;
  profile?: ProfileData;
  error?: string;
  savedOffline: boolean;
  needsSync?: boolean;
}

export class OfflineFirstProfileService {
  private static instance: OfflineFirstProfileService;

  static getInstance(): OfflineFirstProfileService {
    if (!OfflineFirstProfileService.instance) {
      OfflineFirstProfileService.instance = new OfflineFirstProfileService();
    }
    return OfflineFirstProfileService.instance;
  }

  /**
   * Get user profile - Always from IndexedDB (offline-first)
   */
  async getProfile(userId: string): Promise<ProfileResult> {
    try {
      console.log(
        '[OfflineFirstProfileService] Getting profile for user:',
        userId
      );

      const result = await offlineFirstDataManager.getData<ProfileData>(
        'base_profiles',
        userId
      );

      if (result.success && result.data) {
        return {
          success: true,
          profile: result.data as ProfileData,
          fromCache: true,
          needsSync: result.needsSync,
        };
      } else {
        // No profile found in IndexedDB, return empty structure
        return {
          success: true,
          profile: undefined,
          fromCache: true,
          needsSync: false,
        };
      }
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to get profile:',
        error
      );
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get profile',
        fromCache: true,
      };
    }
  }

  /**
   * Update user profile - Always to IndexedDB first
   */
  async updateProfile(
    userId: string,
    updates: Partial<Pick<ProfileData, 'full_name' | 'phone' | 'avatar_url'>>,
    userEmail: string
  ): Promise<ProfileUpdateResult> {
    try {
      console.log(
        '[OfflineFirstProfileService] Updating profile for user:',
        userId
      );

      // Get existing profile or create new one
      const existingResult = await this.getProfile(userId);

      const profileData: ProfileData = {
        id: userId,
        full_name: '',
        email: userEmail,
        phone: null,
        avatar_url: null,
        created_at: new Date().toISOString(),
        ...(existingResult.profile || {}),
        ...updates,
        updated_at: new Date().toISOString(), // Always update timestamp
      };

      // Save to IndexedDB (primary storage)
      const saveResult = await offlineFirstDataManager.saveData(
        'base_profiles',
        profileData,
        'UPDATE'
      );

      if (saveResult.success) {
        return {
          success: true,
          profile: saveResult.data as ProfileData,
          savedOffline: true,
          needsSync: saveResult.needsSync,
        };
      } else {
        return {
          success: false,
          error: saveResult.error || 'Failed to save profile',
          savedOffline: false,
        };
      }
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to update profile:',
        error
      );
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to update profile',
        savedOffline: false,
      };
    }
  }

  /**
   * Create initial profile for new user
   */
  async createInitialProfile(user: User): Promise<ProfileResult> {
    try {
      console.log(
        '[OfflineFirstProfileService] Creating initial profile for user:',
        user.id
      );

      const profile: ProfileData = {
        id: user.id,
        full_name:
          user.user_metadata?.full_name || user.user_metadata?.name || '',
        email: user.email || '',
        phone: user.phone || null,
        avatar_url: user.user_metadata?.avatar_url || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Save to IndexedDB (primary storage)
      const saveResult = await offlineFirstDataManager.saveData(
        'base_profiles',
        profile,
        'INSERT'
      );

      if (saveResult.success) {
        return {
          success: true,
          profile: saveResult.data as ProfileData,
          fromCache: true,
          needsSync: saveResult.needsSync,
        };
      } else {
        return {
          success: false,
          error: saveResult.error || 'Failed to create profile',
          fromCache: true,
        };
      }
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to create initial profile:',
        error
      );
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to create initial profile',
        fromCache: true,
      };
    }
  }

  /**
   * Get sync status for profile
   */
  async getProfileSyncStatus(userId: string): Promise<{
    needsSync: boolean;
    lastModified?: string;
    pendingChanges: number;
  }> {
    try {
      const result = await this.getProfile(userId);
      const syncStatus = offlineFirstDataManager.getSyncStatus();

      return {
        needsSync: result.needsSync || false,
        lastModified: result.profile?.updated_at,
        pendingChanges: syncStatus.pending,
      };
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to get sync status:',
        error
      );
      return {
        needsSync: false,
        pendingChanges: 0,
      };
    }
  }

  /**
   * Force sync profile changes to Supabase
   */
  async syncProfile(): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[OfflineFirstProfileService] Forcing profile sync...');

      const syncResult = await offlineFirstDataManager.forcSync();

      return {
        success: syncResult.success,
        error:
          syncResult.errors.length > 0
            ? syncResult.errors.join(', ')
            : undefined,
      };
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to sync profile:',
        error
      );
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to sync profile',
      };
    }
  }

  /**
   * Check if profile exists in IndexedDB
   */
  async profileExists(userId: string): Promise<boolean> {
    try {
      const result = await this.getProfile(userId);
      return result.success && !!result.profile;
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to check profile existence:',
        error
      );
      return false;
    }
  }

  /**
   * Get all profiles (for admin/debugging)
   */
  async getAllProfiles(): Promise<ProfileData[]> {
    try {
      const result =
        await offlineFirstDataManager.getData<ProfileData[]>('base_profiles');

      if (result.success && result.data) {
        return Array.isArray(result.data) ? result.data : [result.data];
      }

      return [];
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to get all profiles:',
        error
      );
      return [];
    }
  }

  /**
   * Delete profile (marks for deletion, syncs when online)
   */
  async deleteProfile(
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(
        '[OfflineFirstProfileService] Deleting profile for user:',
        userId
      );

      // Mark for deletion in sync queue
      const result = await offlineFirstDataManager.saveData(
        'base_profiles',
        { id: userId },
        'DELETE'
      );

      return {
        success: result.success,
        error: result.error,
      };
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to delete profile:',
        error
      );
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to delete profile',
      };
    }
  }

  /**
   * Get profile statistics
   */
  async getProfileStats(): Promise<{
    totalProfiles: number;
    pendingSync: number;
    lastSyncTime?: string;
  }> {
    try {
      const allProfiles = await this.getAllProfiles();
      const syncStatus = offlineFirstDataManager.getSyncStatus();

      const pendingProfiles = allProfiles.filter(p => p._sync_pending).length;

      return {
        totalProfiles: allProfiles.length,
        pendingSync: pendingProfiles,
        lastSyncTime: allProfiles
          .filter(p => !p._sync_pending)
          .sort(
            (a, b) =>
              new Date(b.updated_at).getTime() -
              new Date(a.updated_at).getTime()
          )[0]?.updated_at,
      };
    } catch (error) {
      console.error(
        '[OfflineFirstProfileService] Failed to get profile stats:',
        error
      );
      return {
        totalProfiles: 0,
        pendingSync: 0,
      };
    }
  }
}

// Export singleton instance
export const offlineFirstProfileService =
  OfflineFirstProfileService.getInstance();
